import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Users,
  Stethoscope,
  FileText,
  Loader2,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  {
    id: "parents-caregivers",
    label: "Parents & Caregivers",
    icon: Users,
    description: "Courses designed for parents, carers, and families navigating complexity.",
  },
  {
    id: "allied-health",
    label: "Allied Health Professionals",
    icon: Stethoscope,
    description: "Professional development for clinicians, support coordinators, and allied health workers.",
  },
];

function CourseCard({ course }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="glass-card p-8 md:p-10"
      data-testid={`course-${course.id}`}
    >
      {course.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {course.tags.map((tag) => (
            <span key={tag} className="px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium bg-[#4A6741]/10 text-[#4A6741]">
              {tag}
            </span>
          ))}
        </div>
      )}
      <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-normal text-[#1A1C19] mb-3 leading-tight">
        {course.title}
      </h3>
      {course.subtitle && (
        <p className="text-sm font-medium text-[#4A6741] mb-4">{course.subtitle}</p>
      )}
      <p className="text-sm font-light text-[#5C635A] leading-relaxed mb-8">
        {course.description}
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        {course.link && (
          <a href={course.link} target="_blank" rel="noopener noreferrer" className="btn-pill text-[11px] justify-center" data-testid={`course-enrol-${course.id}`}>
            View Course <ArrowRight size={13} />
          </a>
        )}
        {course.pdf_review && (
          <a href={course.pdf_review} target="_blank" rel="noopener noreferrer" className="btn-pill-outline text-[11px] justify-center" data-testid={`course-pdf-${course.id}`}>
            <FileText size={13} /> Course Overview PDF
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function CoursesPage() {
  const [activeCategory, setActiveCategory] = useState("parents-caregivers");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchCourses() {
      try {
        const res = await axios.get(`${API}/courses`);
        setCourses(res.data.courses);
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    }
    fetchCourses();
  }, []);

  const filteredCourses = courses.filter((c) => c.category === activeCategory);
  const activeInfo = categories.find((c) => c.id === activeCategory);

  const getCategoryCount = (catId) => courses.filter((c) => c.category === catId).length;

  return (
    <>
      <Helmet>
        <title>Courses | ALIGN&bull;</title>
        <meta name="description" content="Professional courses from ALIGN• for parents, caregivers, and allied health professionals." />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: "#F7F5F0" }} data-testid="courses-page">
        <header className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-40">
          <div className="max-w-5xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/")} className="text-[#5C635A] hover:text-[#4A6741] transition-colors" data-testid="courses-back-button">
                <ArrowLeft size={18} />
              </button>
              <button onClick={() => navigate("/")} className="font-['Cormorant_Garamond'] text-2xl font-light tracking-tight text-[#1A1C19] hover:text-[#4A6741] transition-colors">
                ALIGN<span className="text-[#4A6741]">&bull;</span>
              </button>
            </div>
            <p className="text-[#5C635A] font-['Manrope'] text-xs uppercase tracking-widest font-medium">Courses</p>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-6">Learning</p>
            <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light tracking-tight text-[#1A1C19] mb-6">Our Courses</h1>
            <p className="text-lg font-light text-[#5C635A] max-w-2xl mx-auto">
              Research-backed courses designed to bridge the gap between understanding and everyday practice — for families and professionals alike.
            </p>
          </motion.div>

          {/* Category Tabs */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-12">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              const count = getCategoryCount(cat.id);
              return (
                <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className={`flex-1 flex items-center gap-3 px-6 py-4 rounded-2xl transition-all text-left ${isActive ? "bg-[#4A6741] text-white shadow-lg shadow-[#4A6741]/15" : "bg-white/70 backdrop-blur-md border border-white/40 text-[#5C635A] hover:border-[#4A6741]/20"}`} data-testid={`category-tab-${cat.id}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isActive ? "bg-white/20" : "bg-[#4A6741]/10"}`}>
                    <Icon size={18} className={isActive ? "text-white" : "text-[#4A6741]"} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isActive ? "text-white" : "text-[#1A1C19]"}`}>{cat.label}</p>
                    <p className={`text-[11px] font-light ${isActive ? "text-white/70" : "text-[#5C635A]"}`}>
                      {count} course{count !== 1 ? "s" : ""} available
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {activeInfo && <p className="text-sm font-light text-[#5C635A] mb-8">{activeInfo.description}</p>}

          {/* Course List */}
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={24} className="animate-spin text-[#4A6741]" />
              </div>
            ) : filteredCourses.length > 0 ? (
              filteredCourses.map((course) => <CourseCard key={course.id} course={course} />)
            ) : (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-12 text-center" data-testid="no-courses-message">
                <div className="w-14 h-14 rounded-2xl bg-[#4A6741]/10 flex items-center justify-center mx-auto mb-5">
                  {activeCategory === "allied-health" ? <Stethoscope size={24} className="text-[#4A6741]" strokeWidth={1.5} /> : <Users size={24} className="text-[#4A6741]" strokeWidth={1.5} />}
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-normal text-[#1A1C19] mb-3">Coming Soon</h3>
                <p className="text-sm font-light text-[#5C635A] max-w-md mx-auto">
                  We're developing courses for {activeCategory === "allied-health" ? "allied health professionals" : "parents and caregivers"}. Join our waiting list to be notified when they launch.
                </p>
                <button onClick={() => navigate("/")} className="btn-pill-outline text-[11px] mt-6">Join Waiting List</button>
              </motion.div>
            )}
          </div>

          <div className="mt-16 pt-8 border-t border-[#EAE6DF] text-center">
            <button onClick={() => navigate("/")} className="text-xs text-[#5C635A] hover:text-[#4A6741] transition-colors inline-flex items-center gap-2">
              <ArrowLeft size={12} /> Back to ALIGN&bull;
            </button>
          </div>
        </main>
      </div>
    </>
  );
}
